<template>
  <div>
    <div class="date-time-ct">
      <form-item
        :label="$t('CampaignForm_Schedule_Repeat_Starts')"
        class="input start-date"
      >
        <div class="starts-from">
          {{ $t('CampaignForm_Schedule_Repeat_From') }}
        </div>
        <el-date-picker
          format="MMMM d, yyyy"
          value-format="yyyy-MM-dd"
          v-model="model.start_date"
          class="date-input"
          :picker-options="pickerOptions"
          @change="dateChanged('start_date', $event)"
        />
      </form-item>
      <form-item
        :label="$t('CampaignForm_Schedule_Repeat_Time')"
        class="time-input input"
      >
        <el-time-picker
          format="HH:mm"
          value-format="HH:mm"
          v-model="model.send_time"
          @change="timeChanged('send_time', $event)"
        />
      </form-item>
      <form-item
        :label="$t('CampaignForm_Schedule_Repeat_Timezone')"
        class="timezone-select"
      >
        <el-select v-model="model.timezone" filterable clearable>
          <el-option
            v-for="(label, value) in timezones"
            :key="value"
            :value="value"
            :label="label"
          >
            <span v-tooltip>{{ label }}</span>
          </el-option>
        </el-select>
      </form-item>
    </div>
    <div class="separator"></div>
    <div class="section-ct">
      <custom-schedule-component :model="model" />
    </div>
    <div class="section-ct">
      <form-item :label="$t('CampaignForm_Schedule_Repeat_Ends')">
        <radio-group v-model="endsOn" inline>
          <radio
            :border="true"
            :label="false"
            :title="$t('CampaignForm_Schedule_Repeat_Never')"
            class="radio-never"
          />
          <radio-custom :border="true" :label="true">
            <div class="radio-input">
              <div class="input-description">
                {{ $t('CampaignForm_Schedule_Repeat_On') }}
              </div>
              <el-date-picker
                :disabled="!endsOn"
                format="MMMM d, yyyy"
                value-format="yyyy-MM-dd"
                v-model="model.end_date"
                class="date-input"
                :picker-options="pickerOptions"
                @change="dateChanged('end_date', $event)"
              />
            </div>
          </radio-custom>
        </radio-group>
      </form-item>
    </div>
    <div class="separator"></div>
    <div class="prepare-upfront-ct">
      <el-checkbox
        class="prepare-upfront-label"
        :label="$t('CampaignForm_Schedule_Repeat_PrepareUpfront')"
        v-model="model.prepare_interval_enabled"
        :true-label="1"
        :false-label="0"
      />
      <div
        v-if="model.prepare_interval_enabled"
        class="prepare-input-number-ct"
      >
        <span class="prepare-upfront-label">{{
          $t('CampaignForm_Schedule_Repeat_PrepareUpfront_Prepare')
        }}</span>
        <el-input-number
          :min="0"
          :max="99"
          :placeholder="
            $t('CampaignForm_Schedule_Repeat_PrepareUpfront_Placeholder')
          "
          :controls="false"
          v-model="model.prepare_interval"
        />
        <span class="prepare-upfront-label">{{
          $t('CampaignForm_Schedule_Repeat_PrepareUpfront_MinutesUpfront')
        }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import FormItem from '@/common/components/form/FormItem'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import RadioCustom from '@/modules/campaign-manage/components/locations/RadioCustom'
import { dayOfWeekLabels } from '@/modules/campaign-manage/enum/Labels'
import moment from 'moment'
import { mapGetters } from 'vuex'
import CustomScheduleComponent from './CustomScheduleComponent.vue'
export default {
  name: 'RepeatComponent',
  components: {
    CustomScheduleComponent,
    RadioCustom,
    Radio,
    RadioGroup,
    FormItem
  },
  props: {
    model: {
      type: Object
    }
  },

  data() {
    return {
      dayOfWeekLabels,
      endsOn: 1,
      sendDays: [1, 2, 3, 4, 5, 6, 7],
      pickerOptions: {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => moment(date).isBefore(this.now, 'date')
      }
    }
  },

  methods: {
    dateChanged(field, newValue) {
      if (newValue === null) {
        this.$nextTick(() => {
          this.model[field] = moment()
            .tz(this.model.timezone)
            .format('Y-MM-DD')
        })
      }
    },
    timeChanged(field, newValue) {
      if (newValue === null) {
        this.$nextTick(() => {
          this.model[field] = moment()
            .tz(this.model.timezone)
            .format('HH:mm')
        })
      }
    }
  },

  computed: {
    ...mapGetters(['timezones'])
  },

  created() {
    this.endsOn = Boolean(this.model.end_date)
    if (!this.model.send_days) {
      this.model.send_days = this.sendDays
    }
  },

  watch: {
    'model.prepare_interval_enabled': {
      handler(selection) {
        if (!selection) {
          this.model.prepare_interval = null
        }
      }
    },

    endsOn(shouldEnd) {
      if (!shouldEnd) {
        this.model.end_date = ''
      }
    }
  }
}
</script>

<style scoped>
.start-date >>> .el-form-item__content {
  display: flex;
  align-items: center;
}

.starts-from {
  margin-right: 10px;
}

.section-ct {
  margin-top: 20px;
}

>>> .el-form-item__label {
  font-weight: 600;
  line-height: 22px;
  font-size: 14px;
}

>>> .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: #1d87c8;
  border-color: #1d87c8;
}

>>> .el-input-number {
  width: 80px;
  margin-left: 16px;
  margin-right: 16px;
}

.el-radio {
  min-height: 60px !important;
}

.radio-input {
  display: flex;
  align-items: center;
}

.radio-input > .el-input {
  width: 200px;
}

.date-time-ct {
  display: flex;
}

.input .date-input {
  width: 200px;
}

.time-input {
  width: 100px;
}

.timezone-select {
  width: 300px;
}

.input {
  margin-right: 16px;
}

.input-description {
  margin-right: 50px;
}

.prepare-upfront-ct {
  margin-top: 32px;
}

.prepare-input-number-ct {
  margin-top: 20px;
}

.prepare-upfront-label,
.starts-from {
  color: #7e8596;
  font-size: 14px;
  line-height: 22px;
}

>>> .el-checkbox__label,
>>> .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #5c6374;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
}

.el-checkbox-group >>> .el-checkbox__label,
.el-checkbox-group >>> .el-checkbox__input.is-checked + .el-checkbox__label {
  font-weight: 400;
}

.radio-never >>> .title {
  font-weight: 400;
}
.separator {
  margin: 24px -20px;
  border-top: 1px solid #ebeef5;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/schedule/RepeatComponent.vue